import { z } from 'zod'

/**
 * The destination-tree STORAGE CONTRACT (#1072), shared by the explorer's
 * Destination Filter (D1) and the standalone destination-search widget (D2)
 * so their stored trees can never drift apart. Pure zod — importable by any
 * widget config without dragging another widget's schema into its bundle.
 *
 * Storage rationale (D1): the tree is stored as a TOP-LEVEL OPTIONAL FLAT
 * ARRAY because only optional non-enum arrays materialize as `undefined` in
 * the admin form; an optional OBJECT materializes `{}` and blank-clobbers
 * feed-global values through getFinalSettings' leaf merge (the #1064 W2
 * feeLines bug). Lenient like searchbarChipRows: parse runs at RENDER time,
 * so a damaged tree must degrade to "no tree", never throw (#858, #1168).
 */
export const destinationNodeLevels = ['island', 'side', 'city', 'city-term', 'resort'] as const

// Settings ship to every visitor and the store outlives the writer: bound what
// a damaged/hostile blob can make every render parse. Real ceiling measured at
// 940 nodes / 57.7 KB on generated data; 3000 leaves headroom without letting
// a megarow blob through. Rows past the cap drop (lenient, like bad rows).
export const MAX_DESTINATION_TREE_ROWS = 3000
export const MAX_DESTINATION_LABEL_LENGTH = 300

export const destinationNodeSchema = z.object({
  level: z.enum(destinationNodeLevels).describe('Which tab level this node belongs to'),
  // positive(): term ids start at 1, and coerce would otherwise turn a
  // null/'' id into 0 and keep a row the per-row safeParse filter should drop.
  id: z.coerce.number().int().positive().describe('Taxonomy term id — what the explorer filter writes'),
  // nonblank/max: an empty or whitespace-only label renders an invisible-but-
  // clickable row and an unbounded one is payload with no rendering value;
  // both drop at the per-row safeParse (derivation never emits either —
  // hygiene catches empty labels).
  label: z.string().min(1).max(MAX_DESTINATION_LABEL_LENGTH).refine((s) => s.trim().length > 0)
    .describe('Prefix-stripped display label'),
  count: z.coerce.number().int().nonnegative().default(0).describe('Properties carrying this term at build time'),
  parent: z.coerce.number().int().positive().optional().describe('Owning island term id; absent when unowned'),
})

/** The stored-tree field, identical for every widget that stores a tree. */
export const destinationTreeField = () =>
  z.preprocess(
    // slice BEFORE the per-row parse: the cap bounds the WORK a hostile blob
    // can cause at render time, not just the rendered length — validating a
    // million junk rows and then slicing would defeat the point.
    (v) => (Array.isArray(v)
      // eslint-disable-next-line no-restricted-syntax -- per-row leniency inside a preprocess wrapper, the sanctioned safeParse use (#1168)
      ? v.slice(0, MAX_DESTINATION_TREE_ROWS).filter((row) => destinationNodeSchema.safeParse(row).success)
      : undefined),
    z.array(destinationNodeSchema).optional(),
  ).describe('Derived destination tree, flat node rows. Written by the Destination Filter panel\'s build action, not hand-edited. Absent = no tree built yet.')

// Length clamps ride in the schema AND at the save boundary
// (clampDestinationStrings in the dashboard's prepareSettingsForSave); slice,
// never reject — a reject would throw at the visitor parse.
const lenientClampedString = (max: number, def: string) =>
  z.preprocess((v) => (typeof v === 'string' ? v.slice(0, max) : undefined), z.string().default(def))

/**
 * The operator-side derivation inputs + build stamp, spread into a widget's
 * `destinations` settings group. Consumed only by the dashboard's Destination
 * Filter panel (src/lib/destination-tree-build.ts — cited per key in the
 * dead-settings consumption manifest).
 *
 * Toolkit Settings::DEFAULTS['prefixes']. A blank prefix means "use the
 * default" — NOT "disable the level": Central's ConvertEmptyStringsToNull
 * turns a saved '' into null, which parses back to the default anyway, so
 * pretending '' is a durable disable would lie after one save round-trip.
 */
export const destinationPrefixFields = {
  prefixIsland: lenientClampedString(20, '(i)')
    .describe('Tag-name prefix marking island terms, e.g. "(i) KAUAI". WP: prefixes[island]'),
  prefixSide: lenientClampedString(20, '(s)')
    .describe('Tag-name prefix marking side-of-island terms, e.g. "(s) East Side". WP: prefixes[side]'),
  prefixResort: lenientClampedString(20, '(r)')
    .describe('Tag-name prefix marking resort terms, e.g. "(r) Kiahuna". WP: prefixes[resort]'),
  // ISO timestamp of the last tree build. Written by the panel alongside the
  // rows in one atomic update; read only by the panel's staleness line.
  // Lenient: a damaged (non-string) stamp must degrade to "never built", not
  // throw the widget's render-time parse (#858/#1168).
  builtAt: z.preprocess((v) => (typeof v === 'string' ? v : undefined), z.string().optional())
    .describe('When the destination tree was last built (ISO). Written by the Destination Filter panel, not hand-edited.'),
}

/**
 * Destination popup GROUP ORDER (#1493 sub-item 1, toolkit destination-filter
 * `views/destination-filter-settings.php:200-246`). The toolkit lets an admin
 * drag / move-up / move-down the destination groups (Islands, Cities,
 * Resorts) and persists the order into `$search['groups']`, which drives
 * display order. The platform had no equivalent: tabs.ts's
 * `computeDestinationTabs` built tabs in a hardcoded islands -> cities ->
 * resorts -> city-terms sequence with no `groupOrder`/`tabOrder` field
 * anywhere in the repo.
 *
 * Order-only, unlike destination-search's `groups` field (which is BOTH
 * selection and order for a different widget) — this array can never hide a
 * tab. An empty tab already drops itself in computeDestinationTabs, so there
 * is nothing here to opt out of.
 *
 * EXPLORER-ONLY: destination-search stores its own, differently-shaped
 * `groups` field (widgets/destination-search/config.ts) and does not use
 * this contract.
 */
export const destinationTabKeys = ['islands', 'cities', 'resorts', 'city-terms'] as const
export type DestinationGroupKey = (typeof destinationTabKeys)[number]

/** tabs.ts's former hardcoded sequence, now the explicit default — an
 *  absent or fully-invalid stored order must reproduce that order exactly,
 *  so an unconfigured feed's popup renders byte-identical to before #1493. */
export const DEFAULT_DESTINATION_GROUP_ORDER: readonly DestinationGroupKey[] = destinationTabKeys

/**
 * Normalizes ANY input into a full, deduped permutation of
 * `destinationTabKeys`: unknown values and duplicates drop, and any group
 * key missing from the input is appended in the default sequence — so a
 * newly introduced tab key (or a partially-stored/legacy order) can never
 * go unrendered for an existing feed. Shared by the zod leaf below AND
 * tabs.ts's `computeDestinationTabs`, so a caller that hands it a raw,
 * un-normalized order (a test, or a future non-schema caller) gets the
 * exact same treatment the saved setting already went through.
 */
export function normalizeDestinationGroupOrder(v: unknown): DestinationGroupKey[] {
  const seen = new Set<DestinationGroupKey>()
  const kept: DestinationGroupKey[] = []
  if (Array.isArray(v)) {
    for (const item of v) {
      if (typeof item === 'string' && (destinationTabKeys as readonly string[]).includes(item)) {
        const key = item as DestinationGroupKey
        if (!seen.has(key)) {
          seen.add(key)
          kept.push(key)
        }
      }
    }
  }
  for (const key of destinationTabKeys) {
    if (!seen.has(key)) kept.push(key)
  }
  return kept
}

/** The stored-order field. Lenient like the other destination leaves: a
 *  damaged/hostile stored value must degrade to the default order, never
 *  throw the widget's render-time parse (#858/#1168 class). */
export const destinationGroupOrderField = () =>
  z.preprocess(
    normalizeDestinationGroupOrder,
    z.array(z.enum(destinationTabKeys)).default([...destinationTabKeys]),
  ).describe('Display order of the destination popup groups. Absent, unknown, or incomplete values fall back to the default islands -> cities -> resorts -> city-terms sequence (missing keys are appended in that order; unknown keys are dropped). WP: no equivalent — platform-only per-feed control (#1493)')
